import { getOrderTiming, parsePrepMinutes, parseReceivedTime } from "./orderTiming";
import { scheduleStationSteps } from "./stationPreparation";

const live = (item) => !["COMPLETED", "READY", "REMOVED", "REJECTED", "CANCELLED"].includes(
  String(item.taskStatus || item.status || "").toUpperCase()
);
const secondsValue = (value) => {
  if (!["number", "string"].includes(typeof value) || String(value).trim() === "") return null;
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};
export const formatPrepSeconds = (seconds) => {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return "Not supplied";
  const value = Math.ceil(seconds);
  const minutes = Math.floor(value / 60), remainder = value % 60;
  return minutes ? `${minutes} min${remainder ? ` ${remainder} sec` : ""}` : `${remainder} sec`;
};
const resolve = (order, item) => {
  const metadata = item.metadata || {};
  const ownSeconds = item.prepTimeSeconds ?? metadata.prepTimeSeconds;
  const ownMinutes = item.prepTime ?? metadata.prepTime ?? metadata.avgPrepTime;
  const estimateSource = ownSeconds != null || ownMinutes != null ? "item"
    : parsePrepMinutes(order.prepTime) != null ? "order" : "missing";
  const prepSeconds = ownSeconds != null ? secondsValue(ownSeconds)
    : ownMinutes != null ? (parsePrepMinutes(ownMinutes) ?? NaN) * 60
    : (parsePrepMinutes(order.prepTime) ?? NaN) * 60;
  const normalized = Number.isFinite(prepSeconds) && prepSeconds > 0 ? prepSeconds : null;
  const receivedAt = parseReceivedTime(item.created ?? order.created);
  return {
    prepSeconds: normalized, estimateSource, receivedAt,
    dueAt: normalized != null && receivedAt != null ? receivedAt + normalized * 1000 : null,
  };
};

export const getItemPreparationEstimate = (order, item) => {
  const { prepSeconds, estimateSource } = resolve(order, item);
  return { prepSeconds, estimateSource };
};

export const sortItemsByDue = (order, items = []) => items.map((item, index) => ({
  item, index, dueAt: live(item) ? resolve(order, item).dueAt : null,
})).sort((a, b) => (a.dueAt ?? Infinity) - (b.dueAt ?? Infinity) || a.index - b.index).map(({ item }) => item);

export function getItemPreparation(order, item, items = [], now = Date.now()) {
  const resolved = resolve(order, item);
  const status = String(item.taskStatus || item.status || "").toUpperCase();
  const terminal = !live(item);
  const liveItems = (items.length ? items : [item]).filter(live);
  const deadlines = [...new Set(liveItems.map((row) => resolve(order, row).dueAt)
    .filter((date) => date != null))].sort((a, b) => a - b);
  const priorityRank = !terminal && resolved.dueAt != null ? deadlines.indexOf(resolved.dueAt) + 1 : null;
  const servingGroupLabel = priorityRank === 1 ? "Serve first" : priorityRank > 1 ? "Serve next" : null;
  const serveTogether = !terminal && resolved.dueAt != null
    && liveItems.filter((row) => resolve(order, row).dueAt === resolved.dueAt).length > 1;
  const priorityLabel = terminal ? (["COMPLETED", "READY"].includes(status) ? "Ready" : "Removed")
    : serveTogether ? "Serve together" : servingGroupLabel || "Timing needed";
  const timing = terminal ? {
    tone: "neutral", label: priorityLabel, display: "—", description: "No active preparation countdown.",
  } : getOrderTiming({ created: resolved.receivedAt, prepTime: resolved.prepSeconds == null ? null : resolved.prepSeconds / 60 }, now);
  // Do not approximate a multi-station recipe with an order-level average.
  const station = !terminal && resolved.estimateSource === "item"
    ? scheduleStationSteps(item.stationSteps ?? item.metadata?.stationSteps, { ...resolved, now })
    : { steps: [], error: terminal ? "" : "Station plan awaiting item and recipe timings." };
  return { ...resolved, priorityRank, priorityLabel, servingGroupLabel, serveTogether, timing, steps: station.steps, stationError: station.error };
}
